<template>
  <div class="Recent">

    <!-- Masthead -->
    <SurveyMasthead
      title="Recent Work"
      subtitle="Comfortable Wearing Many Hats."
      srcImage="/assets/digital/recent/mast-icon-recent-297.png"
      largeImage="/assets/digital/recent/mast-icon-recent-694.png"
      :srcSize="297"
      bgColour="#00A2CB" />

    <!-- Project Details -->
    <div class="details">

      <ContentSection title="Introduction">
        <p>Since 2020 I have been in a challenging, but rewarding, dual role as the Co-founder of Greyridge Games and the Director of Product Design at Relish Studios, which has had me wearing many hats.</p>
        
        <p>My role at Greyridge Games, creating a new games division, is <router-link to="/other/game">detailed here</router-link>.</p>
        
        <p>My work as Director of Product Design on the Relish Studios side of the business has been quite broad. As someone with a very wide skill set, I've been overseeing our product and market strategy, client strategy and growth, our design practice, as well as being closely involved with planning, strategy and design on critical projects when needed.</p>

        <p>In my last performance review, which is scored by both ownership above me and team members below me, I received 41 out of a possible 45 points, with full marks in areas of Ownership, Product Planning, Self Motivation and Impact. I would like to think those scores are reflective of the following work I've taken on at Relish.</p>
      </ContentSection>

      <ContentSection title="Growing The Product Division">
        <p>Traditionally, Relish Studios core market focus has been animation and digital games. One of my core mandates was to broaden our reach in more traditional digital markets (websites, mobile, SaaS platforms (B2C, B2B)), as that side of the business had been somewhat neglected.</p>

        <p>I mapped out a strategy involving a series of steps, each of which would build upon the previous one to form the foundation for transforming this part of the business.</p>

        <ol>
          <li>
            <h3>Product and Market Strategy</h3>
            <p>The first step would entail analyzing and cataloging opportunities with existing clients, then looking at market segments we'd previously worked in but fallen away from, and lastly looking at areas we'd not previously been active in but felt we had the internal expertise to start operating within.</p>

            <p>This process required both a lot of conversations internally with our design and development teams to gauge capacity and capabilities, as well as conversations with existing clients. However, I felt it also required some more formal analysis of the markets we looked to be in. This took the form of me conducting detailed market research in a variety of areas and identifying markets, clients, gauging audience demand, and drawing conclusions around opportunities.</p>

            <ProjectImage
              srcImage="/assets/digital/recent/reports-900.png"
              largeImage="/assets/digital/recent/reports-1800.png"
              :canZoom="false"
              caption="Report samples" />

            <p>The reports I produced became a starting point that helped focus our sales team on these new market opportunities.</p>
          </li>
          <li>
            <h3>Client Strategy and Growth</h3>
            <p>Once we had built out our strategy about who and where we wanted to focus our sales efforts, I then shifted to the “how”. One of the initial big gaps I had identified at Relish early on in my tenure was the fact that we often sold ourselves primarily as an execution provider. We were rarely a full-service partner for our clients; one who they trusted to help grow their product offerings through strategy, market research, branding, and business development opportunities.</p>

            <p>And in the few instances when we were doing that, we were not valuing the work enough and presenting it in the right context to the client so they understood its worth and were more willing to pay for that kind of service.</p>

            <p>So I began working with, and mentoring, some members of our senior sales and design teams, helping them to shift their focus and approach to dealing with our projects and clients. We began to make strategic thought part of much of our project process, focusing on our sales and design teams as being trusted partners whose ideas and advice could more broadly shape the client's product and business development.</p>
          </li>
          <li>
            <h3>Design and Production Practice</h3>
            <p>With those two proceeding pillars in place, I then looked to revamping some more logistical aspects of our design, production, and quoting processes to make us more efficient and competitive, in anticipation of winning more work. Those included:</p>

            <uL>
              <li><strong>Improving out internal toolsets.</strong> We did an inventory of our internal project and client tracking toolsets, and streamlined the number of platforms we used and improved integrations.</li>
              <li><strong>Improving our RFP process.</strong> We worked on streamlining the process to allow us to respond more quickly to RFPs by building a better database of case studies and other boilerplate tools to use in assembling those. We also worked on changes to better separate ourselves from competitors by looking to present additional strategic opportunities to clients in areas they weren't focused on when addressing their needs set out in their RFPs.</li>
              <li><strong>Improving quotes.</strong> We did a review of our rate sheets and resourcing estimates, looking to become more accurate in our quotes and more competitive in our pricing.</li>
            </uL>

          </li>
        </ol>

        <p>The outcome of those efforts is that the digital product division saw a significant uptick in growth, and our internal teams have found another level to the quality of work they produce, and in their confidence to do so. This transformation has seen the product division become a much more holistic provider of services to existing and new clients.</p>
      </ContentSection>

      <ContentSection title="Design &amp; Planning">
        <p>In addition to overseeing the high level direction of our digital product division, I have also been involved in planning and design on some critical in-house products, where my design expertise and planning skills have been required.</p>

        <p>One project that required guidance from me at multiple stages, was our Living Novel apps. These apps present a compelling combination of interactive storey-telling, puzzles, and hidden secrets, geared towards kids to engage them and foster a love of reading and self-discovery.</p>

        <ProjectImage
          srcImage="/assets/digital/recent/wwm-ipad-900.jpg"
          largeImage="/assets/digital/recent/wwm-ipad-1800.jpg"
          :canZoom="true"
          caption="Weirdwood Manor Living Novel" />
        
        <p>When I came on board, our production pipeline had been struggling and we were behind schedule. I stepped in to evaluate that pipeline and helped re-architect our production pipeline with our animation and games teams.</p>

        <ProjectImage
          srcImage="/assets/digital/recent/ln-process-900.png"
          largeImage="/assets/digital/recent/ln-process-1800.png"
          :canZoom="true"
          caption="Re-architected production process" />

        <p>I then worked with our CEO and President to re-envision a new resourcing and team structure for producing these apps, as we were about to begin producing multiple of these apps in parallel. Working against challenging schedule deadlines and resourcing budget constraints, I created a resourcing and production strategy for producing these over the next couple years that involved different, self-contained teams, each specializing in certain aspects of production. Each of these teams could be scheduled much more flexibly, and new teams resourced when needed. This plan quickly allowed us to ramp up to 3 concurrent apps and after adding some additional teams would allow us to work on up to 6 of these apps at the same time.</p>

        <ProjectImage
          srcImage="/assets/digital/recent/ln-production-plan-900.png"
          largeImage="/assets/digital/recent/ln-production-plan-1800.png"
          :canZoom="true"
          caption="2 year production plan for multiple Living Novels" />

        <p>On the heels of this work, further design and strategy was needed for how we would bring these apps to market and grow a healthy ecosystem around them. As someone who believes strongly in <em>doing</em>, when just <em>saying</em> isn't enough, I'm always looking for opportunities to help our teams with challenges when they're struggling. In this case, I laid out some strategy and a series of wires to get the team pointed in the right direction. I envisioned a lightly-gamified ecosystem where parents and educators who were purchasing these apps could follow along with their children's and student's progress. This would also include a recommendation engine based on that progress, to build engagement and urgency for continuing to purchase new content.</p>

        <ProjectImage
          srcImage="/assets/digital/recent/ln-ux-900.jpg"
          largeImage="/assets/digital/recent/ln-ux-1800.jpg"
          :canZoom="true"
          caption="UX and visual design direction" />

        <p>This is just a sample of what I've been up to the last few years. Check out the <router-link to="/other/game">story of the Greyridge Games division</router-link> I built during that time, or browse through other recent non-Relish work, starting with a <router-link to="/digital/hyatt">project with Hyatt Hotels</router-link>.</p>

      </ContentSection>
  
    </div>

    <Pagination
      prevLabel="Greyridge Games"
      nextLabel="Hyatt Hotels"
      prevRoute="Game"
      nextRoute="Hyatt" />


    <Footer />
  </div>
</template>

<script>
import SurveyMasthead from '@/components/SurveyMasthead.vue'
import Footer from '@/components/Footer.vue'
import Pagination from '@/components/Pagination.vue'
import ContentSection from '@/components/ContentSection.vue'
import ProjectImage from '@/components/ProjectImage.vue'

export default {
  name: 'Architecture',
  components: {
    SurveyMasthead,
    Footer,
    Pagination,
    ContentSection,
    ProjectImage
  },
}
</script>

<style lang="scss" scoped>
  .Recent {
    background: #fff;
  }
</style>
